<script setup lang="ts">
/**
 * Modale d'édition d'un objet matériel suivi (`AssetAssignment`) — réservée aux
 * admins (`MATERIAL_WRITE`, contrôlé côté carte appelante).
 *
 * Pré-remplit le formulaire depuis l'objet ouvert : label, responsable, statut,
 * attributs d'inventaire dynamiques (selon le type, secrets 🔒 en `password`),
 * notes et date de rappel. À la validation, appelle `store.updateAsset` puis
 * émet `saved` et ferme.
 *
 * Pattern modale natif `<dialog class="modal">` DaisyUI — cf. OnbHandoverDialog.vue.
 * Le parent récupère un `ref` et appelle `open(asset)` (exposé via defineExpose).
 */
import { computed, ref } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useOnboardingStore } from '@/v2/stores/useOnboardingStore'
import {
  ASSET_CONDITIONS,
  ASSET_STATUS_META,
  ASSET_STATUS_ORDER,
  ASSET_TYPE_BY_CODE,
  RETURN_CONDITIONS,
  attributeLabel,
  isSensitiveAttribute,
} from '@/v2/onboarding/catalog'
import type { AssetAssignment, AssetStatus } from '@/v2/onboarding/types'

const emit = defineEmits<{
  saved: []
}>()

const store = useOnboardingStore()

const dialog = ref<HTMLDialogElement | null>(null)

/** Objet en cours d'édition (mémorisé à l'ouverture). */
const asset = ref<AssetAssignment | null>(null)

/* Champs du formulaire. */
const label = ref('')
const owner = ref('')
const status = ref<AssetStatus>('TO_ORDER')
/** Code d'état à la remise/fourniture ('' = non précisé). */
const condition = ref('')
/** Code d'état à la restitution ('' = non précisé). */
const returnCondition = ref('')
const attributes = ref<Record<string, string>>({})
const notes = ref('')
const reminderAt = ref('')
/* Dates clés du cycle de vie (format YYYY-MM-DD, '' = non renseignée). */
const orderedAt = ref('')
const expectedAt = ref('')
const deliveredAt = ref('')
const returnedAt = ref('')
const saving = ref(false)

/**
 * Clés d'attributs à éditer : celles du catalogue pour le type de l'objet,
 * avec fallback sur les clés effectivement présentes sur l'objet (au cas où le
 * type serait inconnu ou aurait des attributs hors catalogue).
 */
const attributeKeys = computed<string[]>(() => {
  const a = asset.value
  if (!a) return []
  const fromCatalog = ASSET_TYPE_BY_CODE[a.typeCode]?.attributeKeys
  if (fromCatalog && fromCatalog.length) return fromCatalog
  return Object.keys(a.attributes)
})

/** Convertit une date (ISO ou jour) en `YYYY-MM-DD` pour `<input type="date">`. */
function toDateInput(value?: string | null): string {
  if (!value) return ''
  // Déjà au format jour : on garde la partie date sans passer par Date (fuseau).
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value)
  if (match) return match[1]
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}

/** Ouvre la modale en pré-remplissant le formulaire (API publique). */
function open(target: AssetAssignment): void {
  asset.value = target
  label.value = target.label
  owner.value = target.owner ?? ''
  status.value = target.status
  condition.value = target.condition ?? ''
  returnCondition.value = target.returnCondition ?? ''
  notes.value = target.notes ?? ''
  reminderAt.value = toDateInput(target.reminderAt)
  orderedAt.value = toDateInput(target.orderedAt)
  expectedAt.value = toDateInput(target.expectedAt)
  deliveredAt.value = toDateInput(target.deliveredAt)
  returnedAt.value = toDateInput(target.returnedAt)
  // Copie des attributs (sur les clés à éditer) pour ne pas muter l'objet du store.
  const next: Record<string, string> = {}
  for (const key of attributeKeys.value) next[key] = target.attributes[key] ?? ''
  attributes.value = next
  saving.value = false
  dialog.value?.showModal()
}

function close(): void {
  dialog.value?.close()
}

defineExpose({ open })

async function save(): Promise<void> {
  const a = asset.value
  if (!a || saving.value) return
  saving.value = true
  try {
    await store.updateAsset(a.id, {
      label: label.value,
      ownerName: owner.value,
      status: status.value,
      condition: condition.value || null,
      returnCondition: returnCondition.value || null,
      attributes: { ...attributes.value },
      notes: notes.value,
      reminderAt: reminderAt.value || null,
      orderedAt: orderedAt.value || null,
      expectedAt: expectedAt.value || null,
      deliveredAt: deliveredAt.value || null,
      returnedAt: returnedAt.value || null,
    })
    emit('saved')
    close()
  } finally {
    saving.value = false
  }
}

const titleId = 'onb-asset-edit-title'
</script>

<template>
  <dialog ref="dialog" class="modal" :aria-labelledby="titleId">
    <div class="modal-box w-full max-w-2xl max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
      <!-- En-tête + fermeture -->
      <div class="flex items-start justify-between gap-4">
        <div>
          <h2 :id="titleId" class="text-lg font-semibold">Modifier l'objet</h2>
          <p v-if="asset" class="text-sm text-base-content/70">{{ asset.label }}</p>
        </div>
        <button
          type="button"
          class="btn btn-ghost btn-sm motion-reduce:transition-none"
          aria-label="Fermer"
          @click="close"
        >
          <XMarkIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <form class="mt-4 space-y-4" @submit.prevent="save">
        <!-- Label -->
        <div>
          <label class="label" for="onb-asset-edit-label">
            <span class="label-text">Libellé</span>
          </label>
          <input
            id="onb-asset-edit-label"
            v-model="label"
            type="text"
            class="input input-bordered w-full"
          />
        </div>

        <!-- Responsable -->
        <div>
          <label class="label" for="onb-asset-edit-owner">
            <span class="label-text">Responsable</span>
          </label>
          <input
            id="onb-asset-edit-owner"
            v-model="owner"
            type="text"
            class="input input-bordered w-full"
            placeholder="Non assigné"
          />
        </div>

        <!-- Statut -->
        <div>
          <label class="label" for="onb-asset-edit-status">
            <span class="label-text">Statut</span>
          </label>
          <select id="onb-asset-edit-status" v-model="status" class="select select-bordered w-full">
            <option v-for="s in ASSET_STATUS_ORDER" :key="s" :value="s">
              {{ ASSET_STATUS_META[s].label }}
            </option>
          </select>
        </div>

        <!-- État (à la remise / fourniture) -->
        <div>
          <label class="label" for="onb-asset-edit-condition">
            <span class="label-text">État</span>
          </label>
          <select
            id="onb-asset-edit-condition"
            v-model="condition"
            class="select select-bordered w-full"
          >
            <option value="">Non précisé</option>
            <option v-for="c in ASSET_CONDITIONS" :key="c.value" :value="c.value">
              {{ c.label }}
            </option>
          </select>
        </div>

        <!-- État à la restitution -->
        <div>
          <label class="label" for="onb-asset-edit-return-condition">
            <span class="label-text">État à la restitution</span>
          </label>
          <select
            id="onb-asset-edit-return-condition"
            v-model="returnCondition"
            class="select select-bordered w-full"
          >
            <option value="">Non précisé</option>
            <option v-for="c in RETURN_CONDITIONS" :key="c.value" :value="c.value">
              {{ c.label }}
            </option>
          </select>
        </div>

        <!-- Attributs dynamiques -->
        <fieldset v-if="attributeKeys.length" class="rounded-box border border-base-300 bg-base-200/40 p-3">
          <legend class="px-1 text-sm font-medium text-base-content/70">Inventaire</legend>
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div v-for="key in attributeKeys" :key="key">
              <label class="label" :for="`onb-asset-edit-attr-${key}`">
                <span class="label-text inline-flex items-center gap-1">
                  {{ attributeLabel(key) }}
                  <span v-if="isSensitiveAttribute(key)" aria-hidden="true">🔒</span>
                  <span v-if="isSensitiveAttribute(key)" class="sr-only">(donnée sensible)</span>
                </span>
              </label>
              <input
                :id="`onb-asset-edit-attr-${key}`"
                v-model="attributes[key]"
                :type="isSensitiveAttribute(key) ? 'password' : 'text'"
                :autocomplete="isSensitiveAttribute(key) ? 'new-password' : 'off'"
                class="input input-bordered w-full"
              />
            </div>
          </div>
        </fieldset>

        <!-- Notes -->
        <div>
          <label class="label" for="onb-asset-edit-notes">
            <span class="label-text">Notes</span>
          </label>
          <textarea
            id="onb-asset-edit-notes"
            v-model="notes"
            class="textarea textarea-bordered w-full"
            rows="3"
          ></textarea>
        </div>

        <!-- Dates du cycle de vie -->
        <fieldset class="rounded-box border border-base-300 bg-base-200/40 p-3">
          <legend class="px-1 text-sm font-medium text-base-content/70">Dates</legend>
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label class="label" for="onb-asset-edit-ordered">
                <span class="label-text">Date de commande</span>
              </label>
              <input
                id="onb-asset-edit-ordered"
                v-model="orderedAt"
                type="date"
                class="input input-bordered w-full"
              />
            </div>
            <div>
              <label class="label" for="onb-asset-edit-expected">
                <span class="label-text">Date prévue</span>
              </label>
              <input
                id="onb-asset-edit-expected"
                v-model="expectedAt"
                type="date"
                class="input input-bordered w-full"
              />
            </div>
            <div>
              <label class="label" for="onb-asset-edit-delivered">
                <span class="label-text">Date de remise</span>
              </label>
              <input
                id="onb-asset-edit-delivered"
                v-model="deliveredAt"
                type="date"
                class="input input-bordered w-full"
              />
            </div>
            <div>
              <label class="label" for="onb-asset-edit-returned">
                <span class="label-text">Date de restitution</span>
              </label>
              <input
                id="onb-asset-edit-returned"
                v-model="returnedAt"
                type="date"
                class="input input-bordered w-full"
              />
            </div>
            <div>
              <label class="label" for="onb-asset-edit-reminder">
                <span class="label-text">Date de rappel</span>
              </label>
              <input
                id="onb-asset-edit-reminder"
                v-model="reminderAt"
                type="date"
                class="input input-bordered w-full"
              />
            </div>
          </div>
        </fieldset>

        <!-- Actions -->
        <div class="modal-action flex-wrap">
          <button type="button" class="btn btn-ghost motion-reduce:transition-none" @click="close">
            Annuler
          </button>
          <button
            type="submit"
            class="btn btn-primary motion-reduce:transition-none"
            :disabled="saving"
            :aria-busy="saving"
          >
            Enregistrer
          </button>
        </div>
      </form>
    </div>

    <form method="dialog" class="modal-backdrop">
      <button aria-label="Fermer">Fermer</button>
    </form>
  </dialog>
</template>
